import { cn } from '../../cn/cn';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonEdge = 'start' | 'end';
export type IconButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success';

/**
 * As with Button, the colour is one custom property the states read, so a new
 * colour is one line.
 *
 * `default` is the muted grey of unstyled icons. On a saturated surface (the
 * app bars), point `--icon-btn-main` at `currentColor` via className.
 */
const COLORS: Record<IconButtonColor, string> = {
  default: '[--icon-btn-main:var(--color-muted-foreground)]',
  primary: '[--icon-btn-main:var(--color-primary)]',
  secondary: '[--icon-btn-main:var(--color-secondary)]',
  error: '[--icon-btn-main:var(--color-error)]',
  warning: '[--icon-btn-main:var(--color-warning)]',
  info: '[--icon-btn-main:var(--color-info)]',
  success: '[--icon-btn-main:var(--color-success)]',
};

/**
 * A hover tint of the button's colour, and the set's focus ring.
 *
 * The tint mustn't stick after a tap on a phone (Roger uses admin on one).
 * Tailwind v4 emits `hover:` inside `@media (hover: hover)`, which guards it.
 */
const BASE =
  'relative inline-flex shrink-0 items-center justify-center m-0 border-0' +
  ' bg-transparent appearance-none cursor-pointer text-center align-middle' +
  ' rounded-full select-none text-(--icon-btn-main)' +
  ' transition-[background-color,box-shadow] duration-150' +
  ' hover:bg-(--icon-btn-main)/10' +
  ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40' +
  ' disabled:cursor-default disabled:pointer-events-none' +
  ' disabled:bg-transparent disabled:text-disabled-foreground';

/** The spinner drawn over the middle of a loading icon button. */
export const ICON_BUTTON_LOADING_INDICATOR_CLASSES =
  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex';

/** One box per size, with the icon scaled inside it: 28, 36 and 40px. */
const SIZES: Record<IconButtonSize, string> = {
  sm: 'size-7 [&_svg]:size-4',
  md: 'size-9 [&_svg]:size-5',
  lg: 'size-10 [&_svg]:size-6',
};

/**
 * `edge` pulls the button back by half the circle-icon difference, so the icon
 * lines up with the content above.
 */
const EDGES: Record<IconButtonSize, Record<IconButtonEdge, string>> = {
  sm: { start: '-ml-1.5', end: '-mr-1.5' },
  md: { start: '-ml-2', end: '-mr-2' },
  lg: { start: '-ml-2', end: '-mr-2' },
};

export const iconButtonClasses = ({
  size,
  color,
  edge,
  className,
}: {
  size: IconButtonSize;
  color: IconButtonColor;
  edge?: IconButtonEdge;
  className?: string;
}): string =>
  cn(
    BASE,
    COLORS[color],
    SIZES[size],
    edge !== undefined && EDGES[size][edge],
    className,
  );
